// host-helpers.ts
//
// Helpers partagés de normalisation/formatage des hosts.
// Extraits de hosts-console.tsx pour réutilisation par AdminHostsView (onglet
// workspace cockpit) sans dépendre de la page /console legacy.

import { Show } from "solid-js";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface HostItem {
  hostId: string;
  hostname: string;
  displayName?: string | null;
  status: "online" | "idle" | "offline" | "pending" | "error";
  lastSeen: string;
  os?: string;
  system?: string;
  systemVersion?: string;
  agentVersion?: string;
  components?: Record<string, string>;
  tags?: string[];
  ip?: string;
  publicIp?: string;
  privateIp?: string;
  user?: string;
  cpu?: number;
  ram?: number;
  ramTotalBytes?: number;
  ramAvailableBytes?: number;
  ramUsedBytes?: number;
  domainFqdn?: string | null;
  clientLabel?: string | null;
  siteLabel?: string | null;
  riskLevel?: "low" | "medium" | "high" | null;
  channel?: "stable" | "beta" | "alpha";
  persistentSessionCode?: string | null;
  vaultDefaults?: Record<string, string>;
  policy?: Record<string, unknown>;
}

export interface SessionListResponseItem {
  code: string;
  hostId: string;
  state?: string;
}

export type SortKey = keyof HostItem | "client" | "site";
export type SortDir = "asc" | "desc";
export type RawHostItem = Record<string, unknown>;

export interface HostVersionInfo {
  channel: string;
  version: string | null;
  publishedAt: string | null;
}

// ─── Normalisation ─────────────────────────────────────────────────────────

export function optionalString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

export function nonEmptyString(value: unknown): string | undefined {
  const out = optionalString(value)?.trim();
  return out || undefined;
}

export function optionalNumber(value: unknown): number | undefined {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

export function optionalPercent(value: unknown): number | undefined {
  const n = optionalNumber(value);
  if (n === undefined || !Number.isFinite(n)) return undefined;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function normalizeStatus(value: unknown): HostItem["status"] {
  return value === "online" || value === "idle" || value === "offline" || value === "pending" || value === "error"
    ? value
    : "offline";
}

function normalizeRisk(value: unknown): HostItem["riskLevel"] {
  return value === "low" || value === "medium" || value === "high" ? value : null;
}

function normalizeChannel(value: unknown): HostItem["channel"] {
  return value === "beta" || value === "alpha" ? value : "stable";
}

function objectValue(value: unknown): RawHostItem | undefined {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as RawHostItem) : undefined;
}

function firstNonEmpty(...values: unknown[]): string | undefined {
  for (const value of values) {
    const out = nonEmptyString(value);
    if (out) return out;
  }
  return undefined;
}

function firstIp(values: unknown): string | undefined {
  if (!Array.isArray(values)) return undefined;
  for (const item of values) {
    const raw = typeof item === "string" ? item : objectValue(item)?.address;
    const ip = nonEmptyString(raw);
    if (!ip) continue;
    if (ip === "127.0.0.1" || ip === "::1" || ip.startsWith("169.254.") || ip.includes(":")) continue;
    return ip;
  }
  return undefined;
}

function compactVersion(...values: unknown[]): string | undefined {
  const parts: string[] = [];
  for (const value of values) {
    const item = nonEmptyString(value);
    if (!item) continue;
    if (parts.some((existing) => existing.toLowerCase() === item.toLowerCase())) continue;
    parts.push(item);
  }
  return parts.length ? parts.join(" ") : undefined;
}

function platformSystem(platform: unknown): string | undefined {
  const p = nonEmptyString(platform)?.toLowerCase();
  if (!p) return undefined;
  if (p === "win32" || p.includes("windows")) return "Windows";
  if (p === "darwin" || p.includes("mac")) return "macOS";
  if (p.includes("ubuntu")) return "Ubuntu";
  if (p.includes("linux")) return "Linux";
  return nonEmptyString(platform);
}

function systemFromName(name: unknown, platform: unknown): string | undefined {
  const raw = nonEmptyString(name);
  if (!raw) return platformSystem(platform);
  if (/windows/i.test(raw)) {
    return (
      raw
        .replace(/\s+build\s+\S+.*$/i, "")
        .replace(/\s+\d{2}H\d\b.*$/i, "")
        .trim() || "Windows"
    );
  }
  if (/\bmac\s*os\b|macos|darwin/i.test(raw)) return "macOS";
  if (/ubuntu/i.test(raw)) return "Ubuntu";
  if (/debian/i.test(raw)) return "Debian";
  if (/red\s*hat|rhel/i.test(raw)) return "Red Hat";
  if (/rocky/i.test(raw)) return "Rocky Linux";
  if (/alma/i.test(raw)) return "AlmaLinux";
  if (/centos/i.test(raw)) return "CentOS";
  if (/fedora/i.test(raw)) return "Fedora";
  if (/opensuse|suse/i.test(raw)) return "openSUSE";
  if (/linux\s*mint/i.test(raw)) return "Linux Mint";
  if (/arch/i.test(raw)) return "Arch Linux";
  if (/linux/i.test(raw)) return "Linux";
  return platformSystem(platform) ?? raw;
}

function versionFromName(name: unknown, system: unknown): string | undefined {
  const raw = nonEmptyString(name);
  const sys = nonEmptyString(system);
  if (!raw || !sys) return undefined;
  let out = raw;
  if (sys === "Windows Server")
    out = out.replace(/^Microsoft\s+Windows\s+Server\s+/i, "").replace(/^Windows\s+Server\s+/i, "");
  else if (sys === "Windows") out = out.replace(/^Microsoft\s+Windows\s+/i, "").replace(/^Windows\s+/i, "");
  else if (sys === "macOS") out = out.replace(/^mac\s*os\s*x\s+/i, "").replace(/^macos\s+/i, "");
  else out = out.replace(new RegExp(`^${sys.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+`, "i"), "");
  out = out.trim();
  return out && out !== raw ? out : undefined;
}

function recordValue(value: unknown): Record<string, string> | undefined {
  const raw = objectValue(value);
  if (!raw) return undefined;
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(raw)) {
    const s = nonEmptyString(val);
    if (s) out[key] = s;
  }
  return Object.keys(out).length ? out : undefined;
}

function activeUserFromSystemInfo(info?: RawHostItem): string | undefined {
  const sessions = Array.isArray(info?.rdsSessions) ? info.rdsSessions : [];
  let fallback: string | undefined;
  for (const rawSession of sessions) {
    const session = objectValue(rawSession);
    if (!session) continue;
    const user = nonEmptyString(session.userName);
    if (!user) continue;
    const domain = nonEmptyString(session.domainName);
    const label = domain ? `${domain}\\${user}` : user;
    if (!fallback) fallback = label;
    if (nonEmptyString(session.state)?.toLowerCase() === "active") return label;
  }
  return fallback;
}

function memoryPercent(info?: RawHostItem): number | undefined {
  const memory = objectValue(info?.memory);
  const direct = optionalPercent(
    info?.memoryLoadPercent ?? info?.ramPercent ?? memory?.loadPercent ?? memory?.usedPercent,
  );
  if (direct !== undefined) return direct;
  const total = optionalNumber(info?.memoryTotalBytes ?? memory?.totalBytes);
  const available = optionalNumber(info?.memoryAvailableBytes ?? memory?.availableBytes);
  if (!total || total <= 0 || available === undefined) return undefined;
  return optionalPercent(((total - available) / total) * 100);
}

function memoryBytes(info?: RawHostItem) {
  const memory = objectValue(info?.memory);
  const total = optionalNumber(info?.memoryTotalBytes ?? memory?.totalBytes);
  const available = optionalNumber(info?.memoryAvailableBytes ?? memory?.availableBytes);
  const used = total !== undefined && available !== undefined ? Math.max(0, total - available) : undefined;
  return { total, available, used };
}

export function normalizeHostItem(value: unknown, fallback?: HostItem): HostItem {
  const raw = { ...(fallback || {}), ...(objectValue(value) || {}) } as RawHostItem;
  const systemInfo = objectValue(raw.systemInfo) ?? objectValue(raw.system_info);
  const osInfo = objectValue(systemInfo?.os) ?? objectValue(systemInfo?.operatingSystem);
  const networkInfo = objectValue(systemInfo?.network);
  const hostId = String(raw.hostId ?? fallback?.hostId ?? "");
  const hostname = optionalString(raw.hostname) ?? fallback?.hostname ?? hostId;
  const osName = firstNonEmpty(
    raw.os,
    systemInfo?.osName,
    systemInfo?.osDisplayName,
    systemInfo?.osDescription,
    osInfo?.caption,
    osInfo?.name,
    osInfo?.description,
    raw.platformLabel,
    raw.platform,
  );
  const system =
    firstNonEmpty(raw.system, systemInfo?.system, systemInfo?.systemName) ??
    systemFromName(osName, raw.platformLabel ?? raw.platform ?? systemInfo?.platform);
  const systemVersion =
    firstNonEmpty(raw.systemVersion, systemInfo?.systemVersion) ??
    compactVersion(
      systemInfo?.osVersion,
      osInfo?.version,
      systemInfo?.osRelease,
      systemInfo?.release,
      systemInfo?.osBuild ? `build ${systemInfo.osBuild}` : undefined,
      osInfo?.buildNumber ? `build ${osInfo.buildNumber}` : undefined,
    ) ??
    versionFromName(osName, system);
  const privateIp =
    firstNonEmpty(
      raw.privateIp,
      systemInfo?.privateIp,
      systemInfo?.primaryIp,
      systemInfo?.primaryIP,
      systemInfo?.ip,
      networkInfo?.primaryIp,
    ) ?? firstIp(systemInfo?.ipAddresses ?? networkInfo?.ipAddresses ?? networkInfo?.addresses);
  const publicIp = firstNonEmpty(
    raw.publicIp,
    systemInfo?.publicIp,
    systemInfo?.externalIp,
    systemInfo?.wanIp,
    networkInfo?.publicIp,
    networkInfo?.externalIp,
  );
  const ramBytes = memoryBytes(systemInfo);
  return {
    hostId,
    hostname,
    displayName: nonEmptyString(raw.displayName) ?? null,
    status: raw.online === true ? "online" : normalizeStatus(raw.status),
    lastSeen: optionalString(raw.lastSeen) ?? fallback?.lastSeen ?? "",
    os: osName ?? (system && systemVersion ? `${system} ${systemVersion}` : system),
    system,
    systemVersion,
    agentVersion: optionalString(raw.agentVersion) ?? optionalString(raw.version),
    components: recordValue(raw.components ?? systemInfo?.components),
    tags: Array.isArray(raw.tags) ? raw.tags.filter((tag): tag is string => typeof tag === "string") : [],
    ip: firstNonEmpty(raw.ip) ?? publicIp ?? privateIp,
    publicIp,
    privateIp,
    user:
      firstNonEmpty(raw.user, systemInfo?.currentUser, systemInfo?.loggedInUser, systemInfo?.user) ??
      activeUserFromSystemInfo(systemInfo),
    cpu: optionalPercent(
      raw.cpu ?? systemInfo?.cpuLoadPercent ?? systemInfo?.cpuPercent ?? objectValue(systemInfo?.cpu)?.loadPercent,
    ),
    ram: optionalPercent(raw.ram) ?? memoryPercent(systemInfo),
    ramTotalBytes: optionalNumber(raw.ramTotalBytes) ?? ramBytes.total,
    ramAvailableBytes: optionalNumber(raw.ramAvailableBytes) ?? ramBytes.available,
    ramUsedBytes: optionalNumber(raw.ramUsedBytes) ?? ramBytes.used,
    domainFqdn: optionalString(raw.domainFqdn) ?? null,
    clientLabel: optionalString(raw.clientLabel) ?? null,
    siteLabel: optionalString(raw.siteLabel) ?? null,
    riskLevel: normalizeRisk(raw.riskLevel),
    channel: normalizeChannel(raw.channel),
    persistentSessionCode: optionalString(raw.persistentSessionCode) ?? null,
    vaultDefaults:
      raw.vaultDefaults && typeof raw.vaultDefaults === "object" && !Array.isArray(raw.vaultDefaults)
        ? (raw.vaultDefaults as Record<string, string>)
        : {},
    policy: objectValue(raw.policy),
  };
}

// ─── Sessions fetch ────────────────────────────────────────────────────────

export async function fetchSessionsRaw(): Promise<SessionListResponseItem[]> {
  try {
    const r = await fetch("/api/sessions", { credentials: "include" });
    if (!r.ok) return [];
    const data = await r.json();
    if (!Array.isArray(data)) return [];
    return (data as unknown[]).filter((item): item is SessionListResponseItem => {
      if (!item || typeof item !== "object") return false;
      return typeof (item as { code?: unknown }).code === "string";
    }) as SessionListResponseItem[];
  } catch {
    return [];
  }
}

// ─── Format helpers ────────────────────────────────────────────────────────

export function relativeTime(iso: string): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "à l'instant";
  if (diff < 3_600_000) return `il y a ${Math.floor(diff / 60_000)}min`;
  if (diff < 86_400_000) return `il y a ${Math.floor(diff / 3_600_000)}h`;
  return `il y a ${Math.floor(diff / 86_400_000)}j`;
}

export function pctBar(value: number | undefined, _label: string) {
  if (value === undefined || value === null) return <span style={{ color: "var(--text-4)" }}>—</span>;
  const level = value < 70 ? "ok" : value < 90 ? "warn" : "danger";
  const color = level === "ok" ? "var(--ok)" : level === "warn" ? "var(--warn)" : "var(--danger)";
  return (
    <span style={{ display: "inline-flex", "align-items": "center", gap: "4px" }}>
      <span
        style={{
          display: "inline-block",
          width: "40px",
          height: "4px",
          background: "var(--bg-hover)",
          "border-radius": "2px",
          overflow: "hidden",
          "vertical-align": "middle",
        }}
      >
        <span
          style={{
            display: "block",
            height: "100%",
            width: `${value}%`,
            background: color,
            "border-radius": "2px",
          }}
        />
      </span>
      <span style={{ "font-family": "var(--font-mono)", "font-size": "11px", color: "var(--text-3)" }}>{value}%</span>
    </span>
  );
}

export function formatBytes(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return "";
  const gib = value / 1024 ** 3;
  if (gib >= 10) return `${Math.round(gib)} Go`;
  if (gib >= 1) return `${gib.toFixed(1)} Go`;
  return `${Math.round(value / 1024 ** 2)} Mo`;
}

export function ramCell(host: HostItem) {
  const total = formatBytes(host.ramTotalBytes);
  const used = formatBytes(host.ramUsedBytes);
  return (
    <span
      style={{ display: "inline-flex", "align-items": "center", gap: "6px" }}
      title={total ? `${used || "—"} / ${total}` : ""}
    >
      {pctBar(host.ram, "RAM")}
      <Show when={total}>
        <span style={{ "font-family": "var(--font-mono)", "font-size": "10px", color: "var(--text-4)" }}>{total}</span>
      </Show>
    </span>
  );
}

export function displayHostLabel(host: Pick<HostItem, "displayName" | "hostname" | "hostId">): string {
  return host.displayName || host.hostname || host.hostId;
}

export function exportCsv(hosts: HostItem[]) {
  const headers = [
    "hostId",
    "hostname",
    "status",
    "system",
    "systemVersion",
    "user",
    "publicIp",
    "privateIp",
    "cpu",
    "ram",
    "ramTotalBytes",
    "agentVersion",
    "lastSeen",
    "tags",
  ];
  const rows = hosts.map((h) =>
    [
      h.hostId,
      h.hostname,
      h.status,
      h.system ?? h.os ?? "",
      h.systemVersion ?? "",
      h.user ?? "",
      h.publicIp ?? "",
      h.privateIp ?? "",
      h.cpu ?? "",
      h.ram ?? "",
      h.ramTotalBytes ?? "",
      h.agentVersion ?? "",
      h.lastSeen,
      (h.tags ?? []).join("|"),
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `hosts-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function componentEntries(host: HostItem) {
  return Object.entries(host.components ?? {}).sort(([a], [b]) => a.localeCompare(b));
}
